/**
 * Core Utility Functions
 * Reusable utilities that can be used across different layers and services
 */

import type { useNuxtApp } from 'nuxt/app'

/**
 * Check if this is an auth v2 enabled environment
 * @returns boolean - True if auth v2 is enabled
 */
export function isAuthV2Enabled(): boolean {
  // This could be based on environment variables, config, or other indicators
  // For now, return false as default
  return false
}

/**
 * Retry with exponential backoff
 * @param operation - The async operation to retry
 * @param retryAttempt - Current retry attempt (starts at 1)
 * @returns Promise<T> - Result of the operation
 */
export async function retryWithExponentialBackoff<T>(operation: (retryAttempt: number) => Promise<T>, retryAttempt = 1): Promise<T> {
  const MAX_RETRIES = 3
  const INITIAL_DELAY = 700 // 700ms initial delay
  const BACKOFF_RATE = 2 // doubles the delay with each retry

  try {
    return await operation(retryAttempt)
  } catch (error) {
    if (retryAttempt >= MAX_RETRIES) {
      throw error
    }

    // Calculate delay with backoff rate
    const delay = INITIAL_DELAY * Math.pow(BACKOFF_RATE, retryAttempt - 1)

    // Wait before retrying
    await new Promise(resolve => setTimeout(resolve, delay))

    // Retry the operation
    return retryWithExponentialBackoff(operation, retryAttempt + 1)
  }
}

/**
 * Get device information for API requests
 * @returns DeviceInfo object with device details
 */
export function getDeviceInfo(): {
  deviceName: string
  deviceId: string
  deviceType: 'mobile' | 'desktop'
  domainName: string
  ipAddress: string
} {
  if (typeof window === 'undefined') {
    return {
      deviceName: 'server',
      deviceId: 'server',
      deviceType: 'desktop',
      domainName: 'server',
      ipAddress: '0.0.0.1',
    }
  }

  return {
    deviceName: navigator.userAgent,
    deviceId: getDeviceId(),
    deviceType: isMobileTablet() ? 'mobile' : 'desktop',
    domainName: window.location.host,
    ipAddress: '0.0.0.1',
  }
}

/**
 * Get or generate a unique device ID
 * @returns string - Unique device identifier
 */
export function getDeviceId(): string {
  if (typeof window === 'undefined') return 'server'

  try {
    // Simple device ID generation - in production this should be more robust
    let deviceId = localStorage.getItem('deviceId')
    if (!deviceId) {
      deviceId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      localStorage.setItem('deviceId', deviceId)
    }
    return deviceId
  } catch {
    return 'unknown'
  }
}

/**
 * Check if the current device is a mobile or tablet
 * @returns boolean - True if mobile/tablet device
 */
export function isMobileTablet(): boolean {
  if (typeof window === 'undefined') return false

  try {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  } catch {
    return false
  }
}

// Device form factor enum
export enum DeviceFormFactor {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  WEB = 'web',
}

export type DeviceType = 'mobile' | 'tablet' | 'desktop'

// Screen width breakpoints
const MOBILE_WIDTH = 768
const TABLET_WIDTH = 1024

/**
 * Helper function to determine device form factor
 *
 * Note: This function uses window-based detection (client-side only).
 * For SSR-compatible device detection, components should use nuxtApp.$device directly:
 *
 * @example
 * const nuxtApp = useNuxtApp()
 * const isMobile = computed(() => nuxtApp.$device?.isMobile || false)
 *
 * This avoids calling useNuxtApp() in utility functions, which can cause
 * module transformation timeouts during SSR.
 */
const getDeviceFormFactor = (): DeviceFormFactor => {
  // Client-side detection only (window-based)
  // On server, this returns WEB as a safe default
  // Components needing SSR-compatible detection should use nuxtApp.$device
  if (typeof window === 'undefined') return DeviceFormFactor.WEB

  const width = window.innerWidth
  if (width <= MOBILE_WIDTH) {
    return DeviceFormFactor.MOBILE
  } else if (width <= TABLET_WIDTH) {
    return DeviceFormFactor.TABLET
  }
  return DeviceFormFactor.WEB
}

// Individual helpers
export function isMobile(): boolean {
  return getDeviceFormFactor() === DeviceFormFactor.MOBILE
}

export function isTablet(): boolean {
  return getDeviceFormFactor() === DeviceFormFactor.TABLET
}

export function isMobileOrTablet(): boolean {
  const type = getDeviceFormFactor()
  return type === DeviceFormFactor.MOBILE || type === DeviceFormFactor.TABLET
}

export function isDesktop(): boolean {
  return getDeviceFormFactor() === DeviceFormFactor.WEB
}

export function createElementId(page: string, context: string, element: string): string {
  return `${page}-${context}-${element}`
}

// function to get the abbreviation of a number like 1985 can be converted to 1.9K, 100003434 can be converted to 100M, etc.
export function getNumberAbbreviation(num: number): string {
  // Handle invalid inputs that could cause NaN
  if (num == null || isNaN(num) || num === 0) {
    return '0'
  }

  const positiveNumber = Math.abs(num)
  const SI_SYMBOL = ['', 'K', 'M', 'G', 'T', 'P', 'E']
  // what tier? (determines SI symbol)
  const tier = (Math.log10(positiveNumber) / 3) | 0
  // if zero, we don't need a suffix
  if (tier === 0) return positiveNumber.toString()

  // get suffix and determine scale
  const suffix = SI_SYMBOL[tier]
  const scale = Math.pow(10, tier * 3)
  // scale the number
  let scaled = positiveNumber / scale
  scaled = Math.floor(scaled * 10) / 10

  // format number and add suffix
  const formattedNumber = scaled % 1 === 0 ? scaled.toFixed(0) : scaled.toFixed(1)

  if (formattedNumber.length > 4) {
    return formattedNumber.slice(0, 3) + suffix
  }
  return formattedNumber + suffix
}

/**
 * Formats a number with comma separators following en-US locale standards.
 * @param num - The number to format
 * @returns Formatted string (e.g. "1,234,567")
 */
export function formatNumberWithCommas(num: number): string {
  return num.toLocaleString('en-US')
}

export function isMobileOrTabletSSR(nuxtApp: ReturnType<typeof useNuxtApp>): boolean {
  if (!nuxtApp) return false

  const device = nuxtApp.$device as { isMobileOrTablet: boolean }
  return device && device.isMobileOrTablet
}

export function isMobileSSR(nuxtApp: ReturnType<typeof useNuxtApp>): boolean {
  const device = nuxtApp.$device as { isMobile: boolean }
  return device && device.isMobile
}

export function isTabletSSR(nuxtApp: ReturnType<typeof useNuxtApp>): boolean {
  const device = nuxtApp.$device as { isTablet: boolean }
  return device && device.isTablet
}

export function isDesktopSSR(nuxtApp: ReturnType<typeof useNuxtApp>): boolean {
  const device = nuxtApp.$device as { isDesktop: boolean }
  return device && device.isDesktop
}

export async function getContentType(url: string): Promise<string | undefined> {
  try {
    const response = await $fetch.raw(url, {
      method: 'HEAD',
    })
    const contentType = response.headers.get('content-type')
    if (contentType) {
      return contentType
    } else {
      console.log('Content-Type header not found.')
      return
    }
  } catch (error) {
    console.error('Error fetching headers:', error)
  }
}
